import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LdStatusKey } from '@elva/ui';
import { growthKeys } from '@/data/queryKeys';
import { useGrowthQuery } from '@/data/useGrowthQuery';
import { useNotify } from '@/data/notifications';
import { fetchLeadDetail, fetchLeads, recomputeLeadAttribution, updateLeadStatus } from '../api';

export function useLeads() {
  return useGrowthQuery({
    queryKey: growthKeys.leads.list({}),
    queryFn: () => fetchLeads(),
  });
}

export function useLeadDetail(
  backendId: string | undefined,
  numericId: number | null,
  enabled = true,
) {
  return useGrowthQuery({
    queryKey: growthKeys.leads.detail(backendId ?? ''),
    queryFn: () => fetchLeadDetail(backendId!, numericId!),
    enabled: enabled && Boolean(backendId) && numericId != null,
  });
}

export function useLeadMutations() {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [...growthKeys.all, 'leads'] });

  const updateStatus = useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: LdStatusKey; reason?: string }) =>
      updateLeadStatus(id, status, reason),
    onSuccess: () => {
      notify.success('Lead status updated');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const recompute = useMutation({
    mutationFn: recomputeLeadAttribution,
    onSuccess: () => {
      notify.success('Attribution recomputed');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  return { updateStatus, recompute };
}
